import { Navigate, Outlet } from "react-router-dom";
import { useBitsUserContext } from "../../../context/SignedInContext";
import { isSubscriptionNotStarted } from "../../payment/PaymentApi";
import AuthService from "../../auth/AuthService";
import { TripleSpinner } from "../../ui/TripleSpinner";

/**
 * Paywall for the store, applied at the route boundary.
 *
 * The check used to live in an effect inside `Store` — the index route — which
 * left two holes. An effect runs after the render it is guarding, so the page
 * painted before the redirect fired; and the nested routes
 * (`/store/category/:slug`, `/store/product/:id/:slug`, `/store/categories`)
 * carried no check at all, so a deep link, a bookmark or the back button
 * reached them directly. In the UK that was the normal path, not an edge case:
 * `Store` redirects straight to the default category, which is one of the
 * ungated routes.
 *
 * Redirecting during render rather than after it means restricted content is
 * never committed to the DOM.
 *
 * The gate on the nav links stays where it is — that one exists to explain the
 * block with the paywall modal, which a bare redirect cannot do. This is the
 * enforcement.
 */
export const RequireStoreAccess = () => {
  const { wallet } = useBitsUserContext();

  /* `RequireAuth` treats `/store` as auth-optional, so a null wallet means
     either "still loading" or "signed out" and the two need telling apart:
     holding the signed-out case would block it forever, and letting the
     loading case through is the flash this exists to prevent. */
  if (AuthService.isAuthenticated() && wallet == null) {
    return (
      <div className="vh-100 d-flex flex-column align-items-center justify-content-center">
        <TripleSpinner />
      </div>
    );
  }

  if (isSubscriptionNotStarted(wallet)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};
